import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal } from 'antd';
import { message } from '../utils/antdMessage';
import apiFetch from '../utils/apiFetch';
import {
  applyMapLayerClipFilter,
  applyMapLayerContourInterval,
  applyContourLodToRegistry,
  setContourLodConfig,
  bakedContourIntervalOf,
  isMapLayerOnMap,
  normalizeContourDisplayInterval,
  removeMapLayerOverlay,
  setMapLayerOverlayOpacity,
  toggleMapLayerOverlay
} from '../utils/mapLayerOverlay';
import {
  parseMapLayerBounds,
  bboxIntersects,
  themeLabelOf,
  bboxModeForTheme
} from '../utils/orthoToolbox';
import { flyOnOverlayLayerNameClick } from '../utils/overlayNameClickFly';

/** 主题下可叠加图层（等高线不再按全局间距过滤成品列表） */
function filterLayersForTheme(list, theme) {
  return (list || []).filter(
    (ly) => ly.kind === theme && !ly.dryRun && ly.overlayable !== false
  );
}

/**
 * 地图叠加开关：路网/等高线复选、透明度、框选加载主题
 */
export default function useOrthoMapOverlays({
  getMap,
  getMembers,
  onStartInteract,
  expanded,
  setExpanded,
  setPinned,
  /** 打开二级菜单时锁定一级（可标记为自动固定） */
  pinForSecondaryMenu,
  mapPublishedLayers,
  setMapPublishedLayers,
  loadMapPublishedLayers,
  beginBboxSelect,
  isMobile = false
}) {
  const [mapOverlayActive, setMapOverlayActive] = useState({});
  const [mapOverlayOpacity, setMapOverlayOpacity] = useState(0.9);
  /** 路网/等高线主复选：勾选后展开树形菜单 */
  const [mapKindChecked, setMapKindChecked] = useState({ road: false, contour: false });
  /** 路网辅主题：地标 / 建筑 */
  const [mapAuxChecked, setMapAuxChecked] = useState({ poi: false, building: false });
  const [mapKindMenuOpen, setMapKindMenuOpen] = useState({ road: false, contour: false });
  /** 二级菜单内：展开第三方数据源三级面板（占位，待正式 API） */
  const [thirdPartyPanelOpen, setThirdPartyPanelOpen] = useState({ road: false, contour: false });
  /** 每条等高线成品的显示间距 { [layerId]: 5|10|20|30 } */
  const [contourDisplayIntervalById, setContourDisplayIntervalById] = useState({});
  const contourDisplayIntervalByIdRef = useRef({});
  /** bounds=图层范围；device=最近设备（管理后台地图设置） */
  const [overlayNameClickFly, setOverlayNameClickFly] = useState('bounds');
  /** 框选后的加载视野；pendingLoadThemeRef=确认框选后要打开的主题 */
  const loadViewBboxRef = useRef(null);
  const pendingLoadThemeRef = useRef(null);

  useEffect(() => {
    contourDisplayIntervalByIdRef.current = contourDisplayIntervalById;
  }, [contourDisplayIntervalById]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch('/api/admin/map-settings');
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.success === false) return;
        const mode = data?.settings?.overlay_name_click_fly === 'device' ? 'device' : 'bounds';
        if (!cancelled) {
          setOverlayNameClickFly(mode);
          setContourLodConfig(data?.settings?.contour_lod);
        }
      } catch (_) { /* 无权限或失败时保持默认 bounds */ }
    })();
    return () => { cancelled = true; };
  }, []);

  /** 解析某图层当前显示间距（无状态时用切片间距） */
  const resolveLayerDisplayInterval = (ly) => {
    const baked = bakedContourIntervalOf(ly);
    const fromState = contourDisplayIntervalByIdRef.current[ly.id];
    return normalizeContourDisplayInterval(fromState, baked);
  };

  const onPublishedLayerNameClick = (layer) => {
    flyOnOverlayLayerNameClick(getMap?.(), layer, {
      mode: overlayNameClickFly,
      members: typeof getMembers === 'function' ? (getMembers() || []) : [],
      onStartInteract
    });
  };

  // 已发布等高线列表变化时，补齐默认显示间距
  useEffect(() => {
    const contours = (mapPublishedLayers || []).filter((ly) => ly.kind === 'contour');
    if (!contours.length) return;
    setContourDisplayIntervalById((prev) => {
      let changed = false;
      const next = { ...prev };
      contours.forEach((ly) => {
        if (next[ly.id] == null) {
          next[ly.id] = bakedContourIntervalOf(ly);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [mapPublishedLayers]);

  const readJson = async (res) => {
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.success === false) {
      const err = new Error(data.message || data.error || `请求失败 ${res.status}`);
      err.code = data.error;
      err.payload = data;
      throw err;
    }
    return data;
  };

  const syncMapOverlayActive = useCallback(() => {
    const map = getMap?.();
    if (!map) return;
    const next = {};
    (mapPublishedLayers || []).forEach((ly) => {
      next[ly.id] = isMapLayerOnMap(map, ly.id);
    });
    setMapOverlayActive(next);
  }, [getMap, mapPublishedLayers]);

  useEffect(() => {
    if (!expanded) return undefined;
    syncMapOverlayActive();
    const t = setInterval(syncMapOverlayActive, 3000);
    return () => clearInterval(t);
  }, [expanded, syncMapOverlayActive]);

  const togglePublishedMapLayer = (layer) => {
    const map = getMap?.();
    if (!map) {
      message.warning('地图尚未就绪');
      return;
    }
    try {
      onStartInteract?.();
      const { active } = toggleMapLayerOverlay(map, layer);
      if (active) {
        setMapLayerOverlayOpacity(map, layer.id, mapOverlayOpacity);
      }
      setMapOverlayActive((prev) => ({ ...prev, [layer.id]: active }));
      message.success(active ? `已叠加：${layer.name || `#${layer.id}`}` : '已关闭图层');
    } catch (e) {
      message.error(e.message || '叠加失败');
    }
  };

  /** 确保图层在图上；可带 clipBounds 做框选裁剪显示 */
  const ensurePublishedMapLayerOn = (layer, options = {}) => {
    const map = getMap?.();
    if (!map || !layer?.id) return false;
    if (layer.dryRun || layer.overlayable === false) return false;
    const displayInterval = layer.kind === 'contour'
      ? resolveLayerDisplayInterval(layer)
      : undefined;
    try {
      if (isMapLayerOnMap(map, layer.id)) {
        if (options.clipBounds) {
          applyMapLayerClipFilter(map, layer.id, options.clipBounds);
        }
        if (layer.kind === 'contour' && displayInterval != null) {
          applyMapLayerContourInterval(map, layer.id, displayInterval, options.clipBounds);
        }
        setMapOverlayActive((prev) => ({ ...prev, [layer.id]: true }));
        return true;
      }
      onStartInteract?.();
      const { active } = toggleMapLayerOverlay(map, layer, {
        ...options,
        opacity: mapOverlayOpacity,
        displayInterval,
        // 默认不飞全省；仅显式 fitBounds:true 才飞
        fitBounds: options.fitBounds === true
      });
      if (active) setMapLayerOverlayOpacity(map, layer.id, mapOverlayOpacity);
      setMapOverlayActive((prev) => ({ ...prev, [layer.id]: !!active }));
      return !!active;
    } catch (e) {
      console.warn('[map-layer] ensure on failed', e.message);
      return false;
    }
  };

  const requireBboxThenLoadTheme = (theme) => {
    const list = filterLayersForTheme(mapPublishedLayers, theme);
    if (!list.length) {
      message.warning(`暂无已发布的${themeLabelOf(theme)}，请先在管理中切片完成`);
      if (theme === 'poi' || theme === 'building') {
        setMapAuxChecked((prev) => ({ ...prev, [theme]: false }));
      } else {
        setMapKindChecked((prev) => ({ ...prev, [theme]: false }));
      }
      return;
    }
    if (loadViewBboxRef.current) {
      const wgs = loadViewBboxRef.current;
      let onCount = 0;
      list.forEach((ly) => {
        const lb = parseMapLayerBounds(ly.bounds);
        if (lb && !bboxIntersects(wgs, lb)) return;
        if (ensurePublishedMapLayerOn(ly, { fitBounds: false, clipBounds: wgs })) onCount += 1;
      });
      if (theme === 'poi' || theme === 'building') {
        setMapAuxChecked((prev) => ({ ...prev, [theme]: true }));
      } else {
        setMapKindChecked((prev) => ({ ...prev, [theme]: true }));
        try {
          const map = getMap?.();
          map?.fitBounds?.(
            [[wgs.west, wgs.south], [wgs.east, wgs.north]],
            { padding: 48, duration: 600, maxZoom: 15 }
          );
        } catch (_) { /* ignore */ }
      }
      message.success(`已按框选范围显示${themeLabelOf(theme)}（${onCount}，框外已隐藏）`);
      return;
    }
    Modal.confirm({
      title: `加载${themeLabelOf(theme)}前请框选范围`,
      content: '为节省带宽，请先框选当前最需要查看的区域；确认后仅在该视野加载矢量瓦片。',
      okText: '开始框选',
      cancelText: '取消',
      onOk: () => {
        pendingLoadThemeRef.current = theme;
        beginBboxSelect(bboxModeForTheme(theme));
        if (!isMobile) {
          setMapKindMenuOpen((prev) => ({ ...prev, road: theme !== 'contour', contour: theme === 'contour' }));
        }
      },
      onCancel: () => {
        if (theme === 'poi' || theme === 'building') {
          setMapAuxChecked((prev) => ({ ...prev, [theme]: false }));
        } else {
          setMapKindChecked((prev) => ({ ...prev, [theme]: false }));
        }
      }
    });
  };

  /** 一级菜单辅主题：勾选即叠加当前视野，不强制框选、不飞行（避免与路网子菜单混淆） */
  const onMapAuxCheck = async (theme, checked) => {
    if (!checked) {
      turnOffMapKindOverlays(theme);
      setMapAuxChecked((prev) => ({ ...prev, [theme]: false }));
      return;
    }
    setMapAuxChecked((prev) => ({ ...prev, [theme]: true }));
    try {
      // 切片刚完成时列表可能滞后，勾选前先刷新
      const data = await readJson(await apiFetch('/api/map-layers/layers'));
      const fresh = (data.layers || []).map((ly) => ({ ...ly, id: Number(ly.id) }));
      setMapPublishedLayers(fresh);
      const list = fresh.filter(
        (ly) => ly.kind === theme && !ly.dryRun && ly.overlayable !== false
      );
      if (!list.length) {
        message.warning(`暂无已发布的${themeLabelOf(theme)}，请确认切片已成功`);
        setMapAuxChecked((prev) => ({ ...prev, [theme]: false }));
        return;
      }
      let onCount = 0;
      list.forEach((ly) => {
        if (ensurePublishedMapLayerOn(ly, { fitBounds: false })) onCount += 1;
      });
      if (onCount < 1) {
        message.error(`${themeLabelOf(theme)}叠加失败，请看控制台或稍后重试`);
        setMapAuxChecked((prev) => ({ ...prev, [theme]: false }));
        return;
      }
      const z = Number(getMap?.()?.getZoom?.() || 0);
      const needZ = theme === 'building' ? 13 : 12;
      if (z > 0 && z < needZ) {
        message.info(`已加载${themeLabelOf(theme)}；当前缩放约 ${z.toFixed(1)}，建议放大到 ${needZ} 级以上才看得见`);
      } else {
        message.success(`已叠加${themeLabelOf(theme)}（${onCount}）`);
      }
    } catch (e) {
      message.error(e.message || `加载${themeLabelOf(theme)}失败`);
      setMapAuxChecked((prev) => ({ ...prev, [theme]: false }));
    }
  };

  const turnOffMapKindOverlays = (kind) => {
    const map = getMap?.();
    (mapPublishedLayers || []).forEach((ly) => {
      if (ly.kind !== kind) return;
      try {
        removeMapLayerOverlay(map, ly.id, { forget: true });
      } catch (_) { /* ignore */ }
      setMapOverlayActive((prev) => ({ ...prev, [ly.id]: false }));
    });
  };

  /** 切换某一条等高线成品的显示间距（抽稀线） */
  const onLayerContourIntervalChange = (layer, nextInterval) => {
    if (!layer?.id) return;
    const baked = bakedContourIntervalOf(layer);
    const iv = normalizeContourDisplayInterval(nextInterval, baked);
    if (Number(nextInterval) !== iv) {
      message.warning(`该成品切片间距为 ${baked}m，无法显示更密的 ${nextInterval}m 线`);
    }
    setContourDisplayIntervalById((prev) => ({ ...prev, [layer.id]: iv }));
    contourDisplayIntervalByIdRef.current = {
      ...contourDisplayIntervalByIdRef.current,
      [layer.id]: iv
    };
    const map = getMap?.();
    if (map && (mapOverlayActive[layer.id] || isMapLayerOnMap(map, layer.id))) {
      applyMapLayerContourInterval(map, layer.id, iv, loadViewBboxRef.current || undefined, { asBase: true });
      applyContourLodToRegistry(map);
      message.success(`「${layer.name || `#${layer.id}`}」近景按 ${iv}m 显示（拉高后仍按离地高度自动抽稀）`);
    }
  };

  const openKindMenu = (kind) => {
    loadMapPublishedLayers();
    if (typeof pinForSecondaryMenu === 'function') {
      pinForSecondaryMenu();
    } else {
      setPinned(true);
      setExpanded(true);
    }
    setMapKindMenuOpen((prev) => ({ ...prev, [kind]: true }));
  };

  /** 复选框：勾选→先框选再加载；取消→确认是否卸载该类型全部图层 */
  const onMapKindMasterCheck = (kind, checked) => {
    if (checked) {
      if (kind === 'road' || kind === 'contour') {
        requireBboxThenLoadTheme(kind);
        openKindMenu(kind);
        return;
      }
      setMapKindChecked((prev) => ({ ...prev, [kind]: true }));
      openKindMenu(kind);
      return;
    }
    const kindLabel = themeLabelOf(kind);
    const map = getMap?.();
    const activeIds = new Set();
    (mapPublishedLayers || []).forEach((ly) => {
      if (ly.kind !== kind) return;
      if (mapOverlayActive[ly.id] || isMapLayerOnMap(map, ly.id)) activeIds.add(ly.id);
    });
    const activeCount = activeIds.size;
    const applyUncheck = () => {
      turnOffMapKindOverlays(kind);
      setMapKindChecked((prev) => ({ ...prev, [kind]: false }));
      if (kind === 'road') {
        turnOffMapKindOverlays('poi');
        turnOffMapKindOverlays('building');
        setMapAuxChecked({ poi: false, building: false });
      }
      setMapKindMenuOpen((prev) => ({ ...prev, [kind]: false }));
    };
    // 仅叠加 ≥2 项时二次确认；0/1 项直接取消
    if (activeCount < 2) {
      applyUncheck();
      return;
    }
    Modal.confirm({
      title: `取消加载全部${kindLabel}？`,
      content: `是否取消加载所有${kindLabel}？当前地图上已叠加 ${activeCount} 条，确认后将全部关闭。`,
      okText: '取消加载',
      cancelText: '返回',
      okButtonProps: { danger: true },
      onOk: applyUncheck
    });
  };

  /** 文字「路网/等高线」：只开关菜单，绝不改复选框 */
  const onMapKindLabelOpenChange = (kind, open) => {
    if (open) {
      openKindMenu(kind);
      return;
    }
    setMapKindMenuOpen((prev) => ({ ...prev, [kind]: false }));
  };

  const onMapKindTreeCheck = (kind, checkedKeys) => {
    const keys = (Array.isArray(checkedKeys) ? checkedKeys : checkedKeys?.checked || [])
      .map(String)
      .filter((k) => /^\d+$/.test(k));
    const want = new Set(keys);
    const turningOn = (mapPublishedLayers || [])
      .filter((ly) => ly.kind === kind)
      .some((ly) => want.has(String(ly.id)) && !mapOverlayActive[ly.id]);
    if (turningOn && !loadViewBboxRef.current) {
      Modal.confirm({
        title: `加载${themeLabelOf(kind)}前请框选范围`,
        content: '为节省带宽，请先框选需要查看的区域后再勾选图层。',
        okText: '开始框选',
        cancelText: '取消',
        onOk: () => {
          pendingLoadThemeRef.current = kind;
          beginBboxSelect(bboxModeForTheme(kind));
        }
      });
      return;
    }
    let anyOn = false;
    (mapPublishedLayers || []).filter((ly) => ly.kind === kind).forEach((ly) => {
      const id = String(ly.id);
      const on = !!mapOverlayActive[ly.id];
      const should = want.has(id);
      if (should && !on) {
        if (ensurePublishedMapLayerOn(ly, {
          fitBounds: false,
          clipBounds: loadViewBboxRef.current || undefined,
          displayInterval: ly.kind === 'contour' ? resolveLayerDisplayInterval(ly) : undefined
        })) anyOn = true;
      }
      if (!should && on) {
        const map = getMap?.();
        if (map && isMapLayerOnMap(map, ly.id)) {
          try { toggleMapLayerOverlay(map, ly); } catch (_) { /* ignore */ }
        }
        setMapOverlayActive((prev) => ({ ...prev, [ly.id]: false }));
      }
      if (should) anyOn = true;
    });
    if (anyOn) {
      setMapKindChecked((prev) => ({ ...prev, [kind]: true }));
      if (loadViewBboxRef.current) {
        const wgs = loadViewBboxRef.current;
        try {
          getMap?.()?.fitBounds?.(
            [[wgs.west, wgs.south], [wgs.east, wgs.north]],
            { padding: 48, duration: 500, maxZoom: 15 }
          );
        } catch (_) { /* ignore */ }
      }
    } else {
      setMapKindChecked((prev) => ({ ...prev, [kind]: false }));
    }
  };

  const applyMapOverlayOpacity = (value) => {
    const o = Number(value);
    setMapOverlayOpacity(o);
    const map = getMap?.();
    if (!map) return;
    (mapPublishedLayers || []).forEach((ly) => {
      if (mapOverlayActive[ly.id]) setMapLayerOverlayOpacity(map, ly.id, o);
    });
  };

  const confirmMapThemeBbox = async (mode, wgs) => {
    const kind = mode === 'map-contour'
      ? 'contour'
      : mode === 'map-poi'
        ? 'poi'
        : mode === 'map-building'
          ? 'building'
          : 'road';
    const kindLabel = kind === 'contour' ? '等高线'
      : kind === 'poi' ? '地标'
        : kind === 'building' ? '建筑'
          : '路网';
    loadViewBboxRef.current = wgs;
    const candidates = filterLayersForTheme(mapPublishedLayers, kind);
    const hits = candidates.filter((ly) => {
      const lb = parseMapLayerBounds(ly.bounds);
      if (!lb) return true; // 无 bounds 时仍允许加载，靠视野限流
      return bboxIntersects(wgs, lb);
    });
    let onCount = 0;
    hits.forEach((ly) => {
      if (ensurePublishedMapLayerOn(ly, { fitBounds: false, clipBounds: wgs })) onCount += 1;
    });
    if (kind === 'poi' || kind === 'building') {
      setMapAuxChecked((prev) => ({ ...prev, [kind]: true }));
      // 一级辅主题不飞行
    } else {
      setMapKindChecked((prev) => ({ ...prev, [kind]: true }));
      if (!isMobile) {
        setMapKindMenuOpen((prev) => ({ ...prev, [kind]: true }));
      }
      // 路网/等高线框选加载：飞到选区
      try {
        const map2 = getMap?.();
        if (map2) {
          map2.fitBounds(
            [[wgs.west, wgs.south], [wgs.east, wgs.north]],
            { padding: 48, duration: 700, maxZoom: 15 }
          );
        }
      } catch (_) { /* ignore */ }
    }
    pendingLoadThemeRef.current = null;
    if (hits.length < 1) {
      message.warning(`框选范围内没有可叠加的${kindLabel}（请先完成切片发布）`);
    } else {
      message.success(`已按框选范围显示${kindLabel}（${onCount}，框外已隐藏）`);
    }
  };

  return {
    mapOverlayActive,
    setMapOverlayActive,
    mapOverlayOpacity,
    setMapOverlayOpacity,
    mapKindChecked,
    setMapKindChecked,
    mapAuxChecked,
    setMapAuxChecked,
    mapKindMenuOpen,
    setMapKindMenuOpen,
    thirdPartyPanelOpen,
    setThirdPartyPanelOpen,
    contourDisplayIntervalById,
    onLayerContourIntervalChange,
    loadViewBboxRef,
    pendingLoadThemeRef,
    syncMapOverlayActive,
    togglePublishedMapLayer,
    ensurePublishedMapLayerOn,
    requireBboxThenLoadTheme,
    onMapAuxCheck,
    turnOffMapKindOverlays,
    openKindMenu,
    onMapKindMasterCheck,
    onMapKindLabelOpenChange,
    onMapKindTreeCheck,
    onPublishedLayerNameClick,
    applyMapOverlayOpacity,
    confirmMapThemeBbox
  };
}
